import {useCallback, type KeyboardEvent} from 'react';
import type {InsightsTab} from './useInsightsDrawerParams';

export const SKIP_SECONDS = 5;

export interface DrawerShortcutHandlers {
    toggle: () => void;
    skip: (delta: number) => void;
    focusSearch: () => void;
    nextMatch: () => void;
    previousMatch: () => void;
    selectTab: (tab: InsightsTab) => void;
    toggleHelp: () => void;
}

const TABS: Record<string, InsightsTab> = {'1': 'summary', '2': 'transcript', '3': 'ask'};

export const isTypingTarget = (target: EventTarget | null): boolean => {
    if (!(target instanceof HTMLElement)) return false;
    if (target.isContentEditable) return true;
    return ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
};

const isInteractive = (target: EventTarget | null): boolean =>
    target instanceof HTMLElement && Boolean(target.closest('button, a[href], [role="button"], [role="slider"], [role="tab"], [role="menuitem"], summary'));

const isOwnArrowTarget = (target: EventTarget | null): boolean =>
    target instanceof HTMLElement && Boolean(target.closest('[role="slider"], [role="tablist"]'));

export const handleDrawerShortcut = (event: Pick<KeyboardEvent, 'key' | 'target' | 'ctrlKey' | 'metaKey' | 'altKey' | 'shiftKey' | 'defaultPrevented'>, handlers: DrawerShortcutHandlers): boolean => {
    if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) return false;
    if (isTypingTarget(event.target)) return false;
    const {key} = event;
    if (key === 'k' || key === 'K') {
        handlers.toggle();
        return true;
    }
    if (key === ' ') {
        if (isInteractive(event.target)) return false;
        handlers.toggle();
        return true;
    }
    if (key === 'j' || key === 'J') {
        handlers.skip(-SKIP_SECONDS);
        return true;
    }
    if (key === 'l' || key === 'L') {
        handlers.skip(SKIP_SECONDS);
        return true;
    }
    if (key === 'ArrowLeft' || key === 'ArrowRight') {
        if (isOwnArrowTarget(event.target)) return false;
        handlers.skip(key === 'ArrowLeft' ? -SKIP_SECONDS : SKIP_SECONDS);
        return true;
    }
    if (key === '/') {
        handlers.focusSearch();
        return true;
    }
    if (key === 'n') {
        handlers.nextMatch();
        return true;
    }
    if (key === 'N') {
        handlers.previousMatch();
        return true;
    }
    if (key in TABS) {
        handlers.selectTab(TABS[key]);
        return true;
    }
    if (key === '?') {
        handlers.toggleHelp();
        return true;
    }
    return false;
};

export const useDrawerShortcuts = (handlers: DrawerShortcutHandlers, enabled: boolean) =>
    useCallback((event: KeyboardEvent<HTMLElement>) => {
        if (!enabled) return;
        if (!(event.target instanceof Node) || !event.currentTarget.contains(event.target)) return;
        if (handleDrawerShortcut(event, handlers)) event.preventDefault();
    }, [enabled, handlers]);
